import { useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api } from '../lib/api'
import { useAgentAvailable } from '../lib/agent-availability'
import { useBidProfile } from '../lib/bid-profile'
import { useTemplateLibrary } from '../lib/template-library'
import { partLabel, withoutWorkflowRooms } from '../lib/outline'
import type { ArtefactType } from '../lib/artefact-type'
import { NewArtefactModal } from './NewArtefactModal'
import { NewDeckFromPowerPoint } from './ImportPowerPointDialog'
import { BidSiteCard } from './BidSiteCard'
import { JobsSection } from './JobsSection'
import { WorkflowCard } from './WorkflowCard'
import { IconDeck } from './NavIcons'

/**
 * A bid's home page (overview), shown in the workspace centre when no part is open. It
 * welcomes the member into the bid and lists its artefacts and their items as cards —
 * opening one drops into the co-editor. Members can also create an artefact here.
 *
 * The bid-member roster and the AI token-usage widget used to live here too; they moved onto the
 * dedicated Bid Settings page (`onOpenSettings`), alongside the bid's branding.
 */
export function BidHome({
  bid,
  bidName,
  onOpenPart,
  onOpenWorkflow,
  onOpenSettings,
  chatReopen,
}: {
  bid: string | null
  bidName: string
  onOpenPart: (room: string) => void
  onOpenWorkflow: () => void
  onOpenSettings: () => void
  /** The "Open chat" affordance (when the AI chat column is collapsed), passed in so it renders
   *  as a third `.home-cta` pill IN this row — same flex-wrap, same height as "Bid settings" /
   *  "New artefact" — instead of a fixed-position overlay guessing this row's on-screen
   *  coordinates and risking exactly the mismatch/overlap that produced. */
  chatReopen?: ReactNode
}) {
  const { t } = useLang()
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  /** New artefact › From PowerPoint: the name given, the import dialog open. */
  const [fromPptx, setFromPptx] = useState<string | null>(null)
  // The workflow/dashboard card is part of the local-AI surface — hidden when the gate is OFF.
  const agentAvailable = useAgentAvailable()
  // Creating an artefact is a write; a viewer is offered neither entry point into it.
  const { isViewer } = useBidProfile(bid)
  // The template library (AB#269) shares this overview, minus what only a bid has: settings and
  // members, a SharePoint site, the AI workflow. `bidName` already reads "Template library" there.
  const { isLibrary } = useTemplateLibrary(bid)

  const docs = useQuery({
    queryKey: ['docs', bid],
    queryFn: () => api.artefacts(bid as string),
    enabled: Boolean(bid),
    refetchInterval: 4000,
  })
  const baseArtefacts = docs.data?.artefacts ?? []

  // Apply the same synthetic grouping as LeftNav: workflow rooms → 2 grouped artefacts.
  const workingDocsQuery = useQuery({
    queryKey: ['working-docs', bid],
    queryFn: () => api.workingDocs(bid as string),
    enabled: Boolean(bid),
  })
  const workingNodes = workingDocsQuery.data?.nodes ?? []
  const workingRooms = new Set(workingNodes.flatMap((n) => [n.artifactRoom, n.reviewRoom]))
  // Same rule as the explorer, one implementation: workflow rooms out, a genuinely empty artefact
  // kept (it renders as a card with a "—" item list rather than vanishing).
  const manualArtefacts = withoutWorkflowRooms(baseArtefacts, workingRooms)

  // `section` is the artefact-wide § number (see api.PartItem) — workflow rooms own none, but the
  // field has to exist on both shapes for the merged list below to be readable as one.
  type SyntheticPart = { partId: string; name: string; title: string; section?: string | null }
  type SyntheticArtefact = { artefactId: string; title: string; parts: SyntheticPart[]; synthetic: true }

  const syntheticArtefacts: SyntheticArtefact[] = workingNodes.length > 0 ? [
    {
      artefactId: '__travail__',
      title: t('doc_travail'),
      synthetic: true,
      parts: workingNodes.map((n) => ({ partId: n.nodeId, name: n.artifactRoom, title: n.section })),
    },
    {
      artefactId: '__revue__',
      title: t('doc_revue'),
      synthetic: true,
      parts: workingNodes.map((n) => ({ partId: n.nodeId, name: n.reviewRoom, title: n.section })),
    },
  ] : []

  const allArtefacts = [...syntheticArtefacts, ...manualArtefacts]

  /** `templateId` set = a copy of that library template (its type wins — AB#270). */
  async function createArtefact(title: string, type: ArtefactType, templateId: string | null): Promise<void> {
    if (!bid) return
    const res = templateId
      ? await api.createArtefactFromTemplate(bid, title, null, templateId)
      : await api.createArtefact(bid, title, null, type)
    setCreating(false)
    await qc.invalidateQueries({ queryKey: ['docs', bid] })
    // A template with no part yields an artefact with no room to open.
    if (res.room) onOpenPart(res.room)
  }

  return (
    <main className="bidhome" aria-label={`${bidName} — ${t('crumb_overview')}`}>
      <div className="bidhome-head">
        <div>
          {/* No "Opportunity · <id>" chip here any more — the title right below already names the
              bid, and a chip repeating the same name a second time read as a duplicate. No
              artefact-count subtitle either — the grid right below already shows every artefact
              (or its own "no artefacts yet" state), so a sentence just restating that was noise. */}
          <h1>{bidName}</h1>
          {isLibrary && <p>{t('template_library_intro')}</p>}
        </div>
        <div className="bidhome-actions">
          {!isLibrary && (
            <button className="home-cta" disabled={!bid} onClick={onOpenSettings}>
              <span aria-hidden="true">⚙</span> {t('bid_settings_title')}
            </button>
          )}
          {!isViewer && (
            <button className="home-cta" disabled={!bid} onClick={() => setCreating(true)}>
              <span aria-hidden="true">＋</span> {t('new_artefact')}
            </button>
          )}
          {chatReopen}
        </div>
      </div>

      {/* Where the bid's source material lives in SharePoint — a thin row that expands into the
          site browser in place. */}
      {bid && !isLibrary && <BidSiteCard bid={bid} />}

      {/* What runs server-side without the member, and how it ended: their own jobs, or all of them for an admin. */}
      {bid && !isLibrary && <JobsSection key={bid} bid={bid} onOpenPart={onOpenPart} />}

      {/* Availability AND permission: the workflow card's only action (generateNode) writes parts,
          which the backend refuses for a viewer at POST /v1/revisions. */}
      {bid && agentAvailable && !isViewer && !isLibrary && <WorkflowCard bid={bid} onOpenWorkflow={onOpenWorkflow} />}

      {/* Gated on `isFetched`, not `isLoading`: for a query that has never once succeeded,
          `isLoading` reverts to true at the start of every background `refetchInterval` tick,
          which flickered this section between "Loading…" and "No artefacts yet" every few
          seconds. `isFetched` flips once, after the first fetch ever settles (success or
          error), and stays put — so a stalled/erroring poll stays silent instead. */}
      <section className="bidhome-grid" aria-busy={docs.isFetching}>
        {!docs.isFetched && allArtefacts.length === 0 && <div className="home-empty">{t('loading')}</div>}
        {docs.isFetched && allArtefacts.length === 0 && (
          <div className="home-empty">{t('no_artefacts')}</div>
        )}

        {allArtefacts.map((d) => (
          <article key={d.artefactId} className="dochome">
            <div className="dochome-head">
              <span className="dochome-ic" aria-hidden="true">{'type' in d && d.type === 'slides' ? <IconDeck /> : 'type' in d && d.type === 'grid' ? '▦' : '📄'}</span>
              <h2>{d.title}</h2>
              <span className="dochome-ct">{d.parts.length} {t('item_count')}</span>
            </div>
            <ul className="dochome-parts">
              {d.parts.map((p) => (
                <li key={p.name}>
                  <button className="dochome-part" onClick={() => onOpenPart(p.name)}>
                    {/* The § range the part spans ("3", or "3-5" when it holds several), not its
                        rank in the card — the same address the editor and the explorer show.
                        Empty for a part with no heading (the badge styles itself away), which
                        keeps the titles aligned. */}
                    <span className="num">{p.section ?? ''}</span>
                    <span className="lbl">{partLabel(p)}</span>
                    <span className="go" aria-hidden="true">→</span>
                  </button>
                </li>
              ))}
              {d.parts.length === 0 && <li className="dochome-empty">—</li>}
            </ul>
          </article>
        ))}

        {!isViewer && (
          <button className="dochome dochome-new" disabled={!bid} onClick={() => setCreating(true)}>
            <span className="bidtile-plus" aria-hidden="true">＋</span>
            <span className="bidtile-name">{t('new_artefact')}</span>
          </button>
        )}
      </section>

      {creating && (
        <NewArtefactModal
          onSubmit={(v, type, templateId) => createArtefact(v, type, templateId)}
          onFromPowerPoint={(title) => {
            setCreating(false)
            setFromPptx(title)
          }}
          onClose={() => setCreating(false)}
        />
      )}
      {fromPptx !== null && bid && (
        <NewDeckFromPowerPoint
          bid={bid}
          title={fromPptx}
          onCreated={() => void qc.invalidateQueries({ queryKey: ['docs', bid] })}
          onJobEnd={() => void qc.invalidateQueries({ queryKey: ['docs', bid] })}
          onDone={(room) => {
            setFromPptx(null)
            onOpenPart(room)
          }}
          onClose={() => setFromPptx(null)}
        />
      )}
    </main>
  )
}
